/**
 * Admin — Waitlist Dashboard
 * Route: /admin/waitlist
 *
 * Browse marketing waitlist entries, filter by city + interest, search by
 * email substring, and export to CSV. Admin-only (server enforces role).
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Download, Search, Users, MapPin, Trash2, X,
  Trophy, Share2, ChevronDown,
} from 'lucide-react';
import api from '../lib/api';

export default function AdminWaitlist() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('entries'); // 'entries' | 'referrals'
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [data, setData] = useState({ items: [], total: 0, grand_total: 0, by_city: {}, by_interest: {} });
  const [leaderboard, setLeaderboard] = useState(null);
  const [treeCode, setTreeCode] = useState('');
  const [tree, setTree] = useState(null);
  const [cityFilter, setCityFilter] = useState('all');
  const [interestFilter, setInterestFilter] = useState('all');
  const [wasReferredOnly, setWasReferredOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  // Debounce search input to avoid slamming the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/waitlist/entries', {
        params: {
          city: cityFilter,
          interest: interestFilter,
          search: debounced,
          was_referred: wasReferredOnly,
          limit: 500,
        },
      });
      setData(d);
      setLoadError('');
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        navigate('/');
        return;
      }
      setLoadError(e?.response?.data?.detail || 'Could not load waitlist.');
    }
    setLoading(false);
  }, [cityFilter, interestFilter, debounced, wasReferredOnly, navigate]);

  const loadLeaderboard = useCallback(async () => {
    try {
      const { data: d } = await api.get('/waitlist/referrals/leaderboard', { params: { limit: 50 } });
      setLeaderboard(d);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        navigate('/'); return;
      }
      setLeaderboard({ leaderboard: [], total_referred_signups: 0, grand_total: 0 });
    }
  }, [navigate]);

  const loadTree = useCallback(async (code) => {
    setTree(null);
    if (!code) return;
    try {
      const { data: d } = await api.get(`/waitlist/referrals/tree/${encodeURIComponent(code)}`);
      setTree(d);
    } catch (e) {
      setTree({ error: e?.response?.data?.detail || 'Could not load tree.' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === 'referrals' && !leaderboard) loadLeaderboard();
  }, [tab, leaderboard, loadLeaderboard]);
  useEffect(() => { loadTree(treeCode); }, [treeCode, loadTree]);

  const downloadCsv = async () => {
    const token = localStorage.getItem('asap_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    const qs = new URLSearchParams({
      city: cityFilter,
      interest: interestFilter,
      search: debounced,
      was_referred: wasReferredOnly ? 'true' : 'false',
    });
    const res = await fetch(`${base}/api/waitlist/export?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `asap_waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const deleteEntry = async (email) => {
    if (!window.confirm(`Remove ${email} from the waitlist?`)) return;
    try {
      await api.delete(`/waitlist/${encodeURIComponent(email)}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const cityChips = useMemo(() => {
    const entries = Object.entries(data.by_city || {}).sort((a, b) => b[1] - a[1]);
    return entries;
  }, [data]);

  const interestChips = useMemo(() => {
    const entries = Object.entries(data.by_interest || {}).sort((a, b) => b[1] - a[1]);
    return entries;
  }, [data]);

  return (
    <div className="min-h-screen bg-[#F8F7F2]" data-testid="admin-waitlist-page">
      <header className="sticky top-0 bg-[#F8F7F2]/95 backdrop-blur z-10 border-b border-black/[0.04] pt-safe-area">
        <div className="flex items-center gap-2 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 -ml-1.5 active:scale-95"
            aria-label="Back"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold text-[#111]" data-testid="admin-waitlist-title">Waitlist</h1>
            <p className="text-[11px] text-[#9B9EA3]" data-testid="admin-waitlist-count">
              {data.grand_total} total {data.grand_total === 1 ? 'person' : 'people'}
              {data.total !== data.grand_total && ` · ${data.total} matching`}
            </p>
          </div>
          {data.grand_total > 0 && (
            <button
              onClick={downloadCsv}
              data-testid="download-csv"
              className="flex items-center gap-1.5 bg-[#111] text-white text-xs font-bold px-3 py-2 rounded-full active:scale-95"
            >
              <Download size={13} /> CSV
            </button>
          )}
        </div>
      </header>

      <main className="max-w-[760px] mx-auto px-4 py-5 space-y-5 pb-24">
        {loadError && (
          <div
            className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-2xl px-3 py-2.5 text-sm text-[#FF3B30] font-bold"
            data-testid="admin-load-error"
          >
            {loadError}
          </div>
        )}

        {/* Tabs */}
        <div
          data-testid="admin-waitlist-tabs"
          className="bg-white rounded-full border border-black/[0.06] p-1 flex text-[13px] font-bold"
        >
          <button
            onClick={() => setTab('entries')}
            data-testid="tab-entries"
            data-active={tab === 'entries'}
            className={`flex-1 py-2 rounded-full transition ${tab === 'entries' ? 'bg-[#111] text-white' : 'text-[#6B6E73]'}`}
          >
            Entries
          </button>
          <button
            onClick={() => setTab('referrals')}
            data-testid="tab-referrals"
            data-active={tab === 'referrals'}
            className={`flex-1 py-2 rounded-full transition ${tab === 'referrals' ? 'bg-[#111] text-white' : 'text-[#6B6E73]'}`}
          >
            Referrals
          </button>
        </div>

        {tab === 'referrals' ? (
          <ReferralsTab
            leaderboard={leaderboard}
            treeCode={treeCode}
            setTreeCode={setTreeCode}
            tree={tree}
          />
        ) : (<>
        {/* Stats snapshot */}
        <section data-testid="waitlist-stats">
          <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Snapshot</h2>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Total" value={data.grand_total} icon={Users} />
            <Stat
              label="Top city"
              value={cityChips[0]?.[0] || '—'}
              note={cityChips[0] ? `${cityChips[0][1]} joined` : ''}
              icon={MapPin}
            />
            <Stat
              label="Top interest"
              value={interestChips[0]?.[0] ? capitalize(interestChips[0][0]) : '—'}
              note={interestChips[0] ? `${interestChips[0][1]} people` : ''}
            />
          </div>
        </section>

        {/* Search */}
        <section>
          <div className="relative">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9EA3]"
              strokeWidth={2.4}
            />
            <input
              data-testid="waitlist-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email…"
              className="w-full bg-white rounded-2xl border border-black/[0.06] px-9 py-2.5 text-sm font-medium text-[#111] placeholder-[#9B9EA3] focus:outline-none focus:border-[#3F5200]/40"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9B9EA3] active:scale-95"
                aria-label="Clear search"
              >
                <X size={14} strokeWidth={2.5} />
              </button>
            )}
          </div>
        </section>

        {/* City filter */}
        <section data-testid="city-filter">
          <h3 className="text-[11px] font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">City</h3>
          <div className="flex flex-wrap gap-2">
            <FilterChip
              testid="city-chip-all"
              label="All"
              count={data.grand_total}
              active={cityFilter === 'all'}
              onClick={() => setCityFilter('all')}
            />
            {cityChips.map(([city, count]) => (
              <FilterChip
                key={city}
                testid={`city-chip-${city.replace(/\s+/g, '-').toLowerCase()}`}
                label={city}
                count={count}
                active={cityFilter === city}
                onClick={() => setCityFilter(city)}
              />
            ))}
          </div>
        </section>

        {/* Interest filter */}
        <section data-testid="interest-filter">
          <h3 className="text-[11px] font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Interest</h3>
          <div className="flex flex-wrap gap-2">
            <FilterChip
              testid="interest-chip-all"
              label="All"
              count={data.grand_total}
              active={interestFilter === 'all'}
              onClick={() => setInterestFilter('all')}
            />
            {interestChips.map(([interest, count]) => (
              <FilterChip
                key={interest}
                testid={`interest-chip-${interest}`}
                label={capitalize(interest)}
                count={count}
                active={interestFilter === interest}
                onClick={() => setInterestFilter(interest)}
              />
            ))}
          </div>
        </section>

        {/* Referral quick-filter */}
        <section>
          <label className="flex items-center gap-3 bg-white rounded-2xl border border-black/[0.06] px-4 py-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={wasReferredOnly}
              onChange={(e) => setWasReferredOnly(e.target.checked)}
              className="accent-[#111]"
              data-testid="filter-was-referred"
            />
            <span className="text-sm font-bold text-[#111]">Referred signups only</span>
          </label>
        </section>

        {/* Results */}
        <section data-testid="waitlist-list">
          <h3 className="text-[11px] font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">
            Entries {data.total > 0 ? `(${data.items.length} shown)` : ''}
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={18} className="animate-spin text-[#9B9EA3]" />
            </div>
          ) : data.items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-black/[0.06] p-8 text-center" data-testid="waitlist-empty">
              <Users size={26} className="mx-auto text-[#9B9EA3] mb-2" />
              <p className="text-sm font-bold text-[#111]">
                {data.grand_total === 0 ? 'No signups yet' : 'No matches for these filters'}
              </p>
              <p className="text-xs text-[#9B9EA3] mt-1">
                {data.grand_total === 0
                  ? 'Share asaphello.com/welcome to start collecting.'
                  : 'Try clearing search or picking a different city / interest.'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-black/[0.06] divide-y divide-black/[0.04] overflow-hidden">
              {data.items.map((entry) => (
                <Row key={entry.email} entry={entry} onDelete={() => deleteEntry(entry.email)} onOpenTree={(code) => { setTab('referrals'); setTreeCode(code); }} />
              ))}
            </div>
          )}
        </section>
        </>)}
      </main>
    </div>
  );
}

function Row({ entry, onDelete, onOpenTree }) {
  const created = entry.created_at ? formatDate(entry.created_at) : '';
  const invites = entry.invites_count || 0;
  return (
    <div className="px-4 py-3 flex items-start gap-3" data-testid={`waitlist-row-${entry.email}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[#111] break-all">{entry.email}</p>
        <p className="text-[11px] text-[#6B6E73] mt-0.5">
          <span className="font-bold text-[#3F5200]">{entry.city}</span>
          {created && <span className="text-[#9B9EA3]"> · {created}</span>}
        </p>
        {entry.interests && entry.interests.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {entry.interests.map((i) => (
              <span
                key={i}
                className="text-[10px] font-bold uppercase tracking-wide bg-[#F1F0EA] text-[#3F5200] px-2 py-0.5 rounded-full"
              >
                {i}
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {entry.referral_code && (
            <button
              type="button"
              onClick={() => onOpenTree && onOpenTree(entry.referral_code)}
              data-testid={`row-refcode-${entry.email}`}
              className="text-[10px] font-black uppercase tracking-wide bg-[#111] text-[#B6E600] px-2 py-0.5 rounded-full flex items-center gap-1 active:scale-95"
              title="View referrals from this code"
            >
              <Share2 size={10} strokeWidth={2.8} />
              {entry.referral_code}
            </button>
          )}
          {invites > 0 && (
            <span
              data-testid={`row-invites-${entry.email}`}
              className="text-[10px] font-black uppercase tracking-wide bg-[#B6E600] text-[#111] px-2 py-0.5 rounded-full"
              title="Signups they invited"
            >
              +{invites} invited
            </span>
          )}
          {entry.referred_by && (
            <span
              data-testid={`row-referred-by-${entry.email}`}
              className="text-[10px] font-bold uppercase tracking-wide bg-white border border-[#3F5200]/30 text-[#3F5200] px-2 py-0.5 rounded-full"
              title="Invited by this code"
            >
              via {entry.referred_by}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={onDelete}
        className="p-1.5 text-[#9B9EA3] hover:text-[#FF3B30] active:scale-95"
        aria-label="Delete"
        data-testid={`waitlist-delete-${entry.email}`}
      >
        <Trash2 size={15} strokeWidth={2.2} />
      </button>
    </div>
  );
}

function FilterChip({ label, count, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active}
      className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all active:scale-95 border ${
        active
          ? 'bg-[#111] text-white border-[#111]'
          : 'bg-white text-[#111] border-black/[0.08] hover:border-black/25'
      }`}
    >
      {label} <span className={active ? 'text-white/60' : 'text-[#9B9EA3]'}>· {count}</span>
    </button>
  );
}

function ReferralsTab({ leaderboard, treeCode, setTreeCode, tree }) {
  if (!leaderboard) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={18} className="animate-spin text-[#9B9EA3]" />
      </div>
    );
  }
  const totals = leaderboard;
  const rows = totals.leaderboard || [];
  const rate = totals.grand_total > 0
    ? Math.round((totals.total_referred_signups / totals.grand_total) * 100)
    : 0;

  return (
    <div className="space-y-5" data-testid="referrals-tab">
      <section>
        <h2 className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Referral snapshot</h2>
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Total signups" value={totals.grand_total} icon={Users} />
          <Stat label="From referrals" value={totals.total_referred_signups} note={`${rate}% of signups`} icon={Share2} />
          <Stat label="Top code" value={rows[0]?.code || '—'} note={rows[0] ? `${rows[0].invites_count} invited` : ''} icon={Trophy} />
        </div>
      </section>

      <section data-testid="referral-leaderboard">
        <h3 className="text-[11px] font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">Top referrers</h3>
        {rows.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/[0.06] p-8 text-center" data-testid="leaderboard-empty">
            <Trophy size={26} className="mx-auto text-[#9B9EA3] mb-2" />
            <p className="text-sm font-bold text-[#111]">No referrals yet</p>
            <p className="text-xs text-[#9B9EA3] mt-1">
              Once waitlisters start sharing their link, the leaderboard fills in.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-black/[0.06] divide-y divide-black/[0.04] overflow-hidden">
            {rows.map((r, i) => (
              <button
                key={r.code}
                type="button"
                onClick={() => setTreeCode(r.code)}
                data-testid={`leaderboard-row-${r.code}`}
                data-active={treeCode === r.code}
                className={`w-full px-4 py-3 flex items-center gap-3 text-left active:bg-[#F1F0EA] transition ${treeCode === r.code ? 'bg-[#F1F0EA]' : ''}`}
              >
                <div className="w-8 h-8 rounded-full bg-[#111] text-white flex items-center justify-center text-xs font-black shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#111] break-all">{r.email}</p>
                  <p className="text-[11px] text-[#6B6E73] mt-0.5">
                    <span className="font-bold text-[#3F5200]">{r.city || '—'}</span>
                    <span className="text-[#9B9EA3]"> · code {r.code}</span>
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-black text-[#111] leading-none">{r.invites_count}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#9B9EA3] font-bold mt-0.5">invited</p>
                </div>
                <ChevronDown
                  size={16}
                  className={`text-[#9B9EA3] transition-transform ${treeCode === r.code ? '-rotate-90' : ''}`}
                />
              </button>
            ))}
          </div>
        )}
      </section>

      {treeCode && (
        <section data-testid="referral-tree">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[11px] font-bold text-[#9B9EA3] uppercase tracking-wider">
              Invited by {treeCode}
            </h3>
            <button
              onClick={() => setTreeCode('')}
              className="text-[11px] font-bold text-[#3F5200] active:scale-95"
              data-testid="referral-tree-close"
            >
              Close
            </button>
          </div>
          {!tree ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={16} className="animate-spin text-[#9B9EA3]" />
            </div>
          ) : tree.error ? (
            <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-2xl px-3 py-2.5 text-sm text-[#FF3B30] font-bold">
              {tree.error}
            </div>
          ) : (tree.invitees || []).length === 0 ? (
            <div className="bg-white rounded-2xl border border-black/[0.06] p-6 text-center" data-testid="tree-empty">
              <p className="text-sm font-bold text-[#111]">Nobody joined with this code yet.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-black/[0.06] divide-y divide-black/[0.04] overflow-hidden">
              {tree.invitees.map((inv) => (
                <div key={inv.email} className="px-4 py-3" data-testid={`tree-row-${inv.email}`}>
                  <p className="text-sm font-bold text-[#111] break-all">{inv.email}</p>
                  <p className="text-[11px] text-[#6B6E73] mt-0.5">
                    <span className="font-bold text-[#3F5200]">{inv.city}</span>
                    {inv.created_at && <span className="text-[#9B9EA3]"> · {formatDate(inv.created_at)}</span>}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}



function Stat({ label, value, note, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[#9B9EA3]">
        {Icon && <Icon size={11} strokeWidth={2.6} />} {label}
      </div>
      <p className="text-xl font-black text-[#111] mt-1 leading-tight tracking-tight truncate">
        {value ?? '—'}
      </p>
      {note && <p className="text-[10.5px] text-[#9B9EA3] mt-0.5">{note}</p>}
    </div>
  );
}

function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
}
